import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { GET_PRODUCT_DAY_STATS, GET_PRODUCT_WEEKDAY_REPORT } from '../../graphql/reports-queries'
import { dateRangeToUtc, formatEuros } from '../../lib/format'
import {
  DAY_LABELS,
  EmptyData,
  PLANNING_WEEKS,
  QueryError,
  SectionTitle,
  TH,
  TREND_WEEKS,
  TableLoading,
  avgLabel,
  heatCellProps,
  isMissingFieldError,
  isoDayOf,
  labelOf,
  lastNFullWeeks,
  parseYmd,
  productVariantKey,
  weekdayOccurrences,
} from './shared'
import type { OrderSource } from './shared'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DrillTarget {
  productId: number
  /** null until the phase-2 API keys the rollups on variantId. */
  variantId: number | null
  productName: string
  variantName: string
  /** The range the opening tab was showing — drives ONLY the per-day section. */
  start: string
  end: string
}

// All money EX-BTW (API contract 2026-08-30). Null on every money field =
// no order.financial.view; null on cogs/margin with revenue populated =
// recipe data missing (linesWithoutCogsCount > 0). Deal premiums land on the
// parent line's row, or the main dish's row for POS-built deals.
interface ProductDayRow {
  date: string
  productId: number
  variantId: number | null
  productName: string
  variantName: string
  qtySold: number
  revenueExVatCents: number | null
  discountAllocatedExVatCents: number | null
  cogsCents: number | null
  marginCents: number | null
  marginPct: number | null
  linesWithoutCogsCount: number
}

interface PeriodRow {
  productId: number
  variantId: number
  productName: string
  variantName: string
  qtySold: number
}

interface DayHourRow {
  productId: number
  variantId: number
  isoDay: number
  hour: number
  qty: number
}

// Owner-set thresholds (2026-08-30): a day is flagged against the product's own
// pooled period margin, not against an absolute target.
const DIP_RED = 10
const DIP_AMBER = 5

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * POOLED margin: Σ margin over Σ (gross − discount). NEVER the mean of the
 * per-day marginPct values — that would weight a two-unit Tuesday like a
 * forty-unit Saturday. Returns null when money is hidden or the base is empty.
 */
const pooledMarginPct = (rows: ProductDayRow[]): number | null => {
  let margin = 0
  let base = 0
  let seen = false
  for (const r of rows) {
    if (r.marginCents == null) continue
    seen = true
    margin += r.marginCents
    base += (r.revenueExVatCents ?? 0) - (r.discountAllocatedExVatCents ?? 0)
  }
  return seen && base > 0 ? (margin / base) * 100 : null
}

const dateLabel = (date: string): string => {
  const d = parseYmd(date)
  return `${DAY_LABELS[isoDayOf(d) - 1]} ${d.getDate()}/${d.getMonth() + 1}`
}

const marginPctTone = (row: ProductDayRow, pooled: number | null): string => {
  const dip = pooled != null && row.marginPct != null ? pooled - row.marginPct : 0
  if (dip >= DIP_RED || (row.discountAllocatedExVatCents ?? 0) > 0) return 'text-red-600 font-semibold'
  if (dip >= DIP_AMBER) return 'text-amber-600 font-medium'
  return 'text-gray-700'
}

// ─── Panel ────────────────────────────────────────────────────────────────────

const ProductDrillPanel = ({
  drill,
  channel,
  onClose,
}: {
  drill: DrillTarget
  channel: OrderSource | null
  onClose: () => void
}) => {
  const [includeStaff, setIncludeStaff] = useState(false)

  const source = channel ? { order_source: channel } : {}
  const label = labelOf(drill.productName, drill.variantName)

  // ── Section 1: day by day (works on today's API) ──
  const {
    data: dayData,
    loading: dayLoading,
    error: dayError,
  } = useQuery<{ productDayStats: { rows: ProductDayRow[] } }>(GET_PRODUCT_DAY_STATS, {
    variables: {
      filters: { ...dateRangeToUtc(drill.start, drill.end), ...source },
      staffMeals: includeStaff ? 'INCLUDE' : 'EXCLUDE',
    },
    fetchPolicy: 'cache-and-network',
  })

  // ── Sections 2 & 3: weekday patterns (NOT yet deployed) ──
  // Same document and same variables the Planning tab uses, so a drill opened
  // from Planning is answered straight from the cache. cache-first: the window
  // ends last Sunday, so the data cannot change within a session.
  const patternsRange = lastNFullWeeks(PLANNING_WEEKS)
  const trendRange = lastNFullWeeks(TREND_WEEKS)

  const { data: patternData, error: patternError } = useQuery<{
    patterns: { byProduct: PeriodRow[]; byDayHourProduct: DayHourRow[] }
  }>(GET_PRODUCT_WEEKDAY_REPORT, {
    variables: {
      patterns: { ...dateRangeToUtc(patternsRange.start, patternsRange.end), ...source },
      trend: { ...dateRangeToUtc(trendRange.start, trendRange.end), ...source },
    },
    fetchPolicy: 'cache-first',
  })

  const rows = useMemo(() => {
    const key = productVariantKey(drill.productId, drill.variantName)
    const matching = (dayData?.productDayStats?.rows ?? []).filter(
      (r) => productVariantKey(r.productId, r.variantName) === key
    )
    return [...matching].sort((a, b) => a.date.localeCompare(b.date))
  }, [dayData, drill.productId, drill.variantName])

  const occurrences = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        weekdayOccurrences(patternsRange.start, patternsRange.end, i + 1)
      ),
    [patternsRange.start, patternsRange.end]
  )

  const { perDay, perDayHour, hours } = useMemo(() => {
    // A reconciliation-origin drill has no variantId — resolve it from byProduct
    // via the interim name key (see productVariantKey).
    const key = productVariantKey(drill.productId, drill.variantName)
    const variantId =
      drill.variantId ??
      (patternData?.patterns?.byProduct ?? []).find(
        (p) => productVariantKey(p.productId, p.variantName) === key
      )?.variantId ??
      null

    const days = new Array(7).fill(0) as number[]
    const cells = new Map<string, number>()
    let min = 10
    let max = 21

    if (variantId != null) {
      for (const r of patternData?.patterns?.byDayHourProduct ?? []) {
        if (r.productId !== drill.productId || r.variantId !== variantId) continue
        days[r.isoDay - 1] += r.qty
        const cellKey = `${r.isoDay}|${r.hour}`
        cells.set(cellKey, (cells.get(cellKey) ?? 0) + r.qty)
        if (r.hour < min) min = r.hour
        if (r.hour > max) max = r.hour
      }
    }

    const hourList: number[] = []
    for (let h = min; h <= max; h++) hourList.push(h)
    return { perDay: days, perDayHour: cells, hours: hourList }
  }, [patternData, drill.productId, drill.variantId, drill.variantName])

  const pooled = pooledMarginPct(rows)
  // Key on revenue, not margin: permission nulling blanks ALL money, while a
  // missing recipe nulls only cogs/margin — those rows still show their euros.
  const hasMoney = rows.some((r) => r.revenueExVatCents != null)

  const totals = rows.reduce(
    (acc, r) => ({
      qty: acc.qty + r.qtySold,
      revenue: acc.revenue + (r.revenueExVatCents ?? 0),
      discount: acc.discount + (r.discountAllocatedExVatCents ?? 0),
      margin: acc.margin + (r.marginCents ?? 0),
    }),
    { qty: 0, revenue: 0, discount: 0, margin: 0 }
  )

  const maxDayAvg = Math.max(
    ...perDay.map((qty, i) => (occurrences[i] > 0 ? qty / occurrences[i] : 0)),
    0
  )

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-gray-600 bg-opacity-50" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white shadow-xl h-full overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          {/* No range here: the sections below cover DIFFERENT windows (per-day
              follows the opening tab; the weekday sections use the fixed
              pattern weeks) — each section labels its own. */}
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">{label}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-5 space-y-8 flex-1">
          {/* ── 1. Per day ── */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <SectionTitle>
                Per day · {drill.start} – {drill.end}
              </SectionTitle>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeStaff}
                  onChange={(e) => setIncludeStaff(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                />
                Include staff meals
              </label>
            </div>

            {includeStaff && (
              <p className="text-xs text-gray-400 -mt-2 mb-3">
                Quantities include staff meals; those lines carry no revenue, so the money columns
                do not move.
              </p>
            )}

            {dayLoading && !dayData && <TableLoading />}
            {dayError && (
              <QueryError message={`Failed to load per-day figures: ${dayError.message}`} />
            )}
            {!dayError && dayData && rows.length === 0 && <EmptyData />}

            {rows.length > 0 && (
              <>
                {!hasMoney && (
                  <div className="mb-3 bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                    Margin requires financial permission.
                  </div>
                )}
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <TH>Date</TH>
                          <TH right>Qty</TH>
                          {hasMoney && (
                            <>
                              <TH right>Revenue ex VAT</TH>
                              <TH right>Discount</TH>
                              <TH right>Margin</TH>
                              <TH right>Margin %</TH>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {rows.map((r) => (
                          <tr key={`${r.date}|${r.variantId ?? ''}`} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2 font-medium text-gray-900">{dateLabel(r.date)}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                              {r.qtySold}
                            </td>
                            {hasMoney && (
                              <>
                                <td className="px-4 py-2 text-right text-gray-700">
                                  {r.revenueExVatCents != null ? formatEuros(r.revenueExVatCents) : '—'}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  {(r.discountAllocatedExVatCents ?? 0) > 0 ? (
                                    <span className="text-amber-700 font-medium">
                                      −{formatEuros(r.discountAllocatedExVatCents as number)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-700">
                                  {r.marginCents != null ? formatEuros(r.marginCents) : '—'}
                                  {r.linesWithoutCogsCount > 0 && (
                                    <span
                                      className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 ml-1"
                                      title="COGS incomplete — profit overstated"
                                    />
                                  )}
                                </td>
                                <td className={`px-4 py-2 text-right ${marginPctTone(r, pooled)}`}>
                                  {r.marginPct != null ? `${r.marginPct.toFixed(1)}%` : '—'}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        <tr className="border-t border-gray-100 font-medium bg-gray-50">
                          <td className="px-4 py-2 text-gray-900">Period</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                            {totals.qty}
                          </td>
                          {hasMoney && (
                            <>
                              <td className="px-4 py-2 text-right text-gray-900">
                                {formatEuros(totals.revenue)}
                              </td>
                              <td className="px-4 py-2 text-right text-amber-700">
                                {totals.discount > 0 ? `−${formatEuros(totals.discount)}` : '—'}
                              </td>
                              <td className="px-4 py-2 text-right text-gray-900">
                                {formatEuros(totals.margin)}
                              </td>
                              <td className="px-4 py-2 text-right text-gray-900">
                                {pooled != null ? `${pooled.toFixed(1)}%` : '—'}
                              </td>
                            </>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── 2. Weekday spread ── */}
          <div>
            <SectionTitle>Weekday spread</SectionTitle>
            {patternError ? (
              <QueryError
                message={
                  isMissingFieldError(patternError)
                    ? 'Weekday patterns are waiting for an API deploy (byProduct / byDayHourProduct do not exist in the schema yet).'
                    : `Failed to load weekday patterns: ${patternError.message}`
                }
              />
            ) : (
              <>
                <div className="space-y-2">
                  {DAY_LABELS.map((day, i) => {
                    const occ = occurrences[i]
                    const avg = occ > 0 ? perDay[i] / occ : 0
                    const width = maxDayAvg > 0 ? (avg / maxDayAvg) * 100 : 0
                    return (
                      <div key={day} className="flex items-center gap-3">
                        <span className="w-10 text-xs font-medium text-gray-500">{day}</span>
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${width}%`, backgroundColor: '#CA4330' }}
                          />
                        </div>
                        <span className="w-12 text-right text-xs tabular-nums text-gray-600">
                          {avgLabel(avg)}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Average units per weekday over {PLANNING_WEEKS} full weeks ({patternsRange.start} –{' '}
                  {patternsRange.end}), staff meals included.
                </p>
              </>
            )}
          </div>

          {/* ── 3. Hour × weekday ── */}
          <div>
            <SectionTitle>
              Hour × weekday · last {PLANNING_WEEKS} full weeks ({patternsRange.start} –{' '}
              {patternsRange.end})
            </SectionTitle>
            {patternError ? (
              <QueryError message="The hour × weekday grid is unavailable for the same reason as the weekday spread above." />
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 p-4 overflow-x-auto">
                <div
                  className="grid gap-px"
                  style={{ gridTemplateColumns: `3rem repeat(${hours.length}, minmax(2.25rem, 1fr))` }}
                >
                  <div />
                  {hours.map((h) => (
                    <div key={`head-${h}`} className="text-[11px] text-gray-400 text-center">
                      {h}
                    </div>
                  ))}

                  {DAY_LABELS.map((day, i) => {
                    const isoDay = i + 1
                    const occ = occurrences[i]
                    // Scaled per ROW so a busy Saturday cannot flatten Tuesday.
                    let rowMax = 0
                    for (const h of hours) {
                      const qty = perDayHour.get(`${isoDay}|${h}`) ?? 0
                      if (qty > rowMax) rowMax = qty
                    }

                    return (
                      <div key={day} className="contents">
                        <div className="text-xs font-medium text-gray-500 flex items-center">{day}</div>
                        {hours.map((h) => {
                          const qty = perDayHour.get(`${isoDay}|${h}`) ?? 0
                          const avg = occ > 0 ? qty / occ : 0
                          const cell = heatCellProps(qty, rowMax)
                          return (
                            <div
                              key={`${day}-${h}`}
                              className={cell.className}
                              style={cell.style}
                              title={`${day} ${h}:00 — ${qty} units over ${occ} ${day}s`}
                            >
                              {qty === 0 ? '' : avgLabel(avg)}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDrillPanel
